import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

/**
 * Welk tabblad van de klantpagina open staat, en waar in het rapport: uit de
 * adresbalk, zodat elke stand een link is die werkt, en de knoppen Terug en
 * Vooruit van de browser gewoon doen wat ze zeggen.
 *
 * - geen `#` of `#overzicht`: het overzicht;
 * - `#kosten`: de kosten per onderdeel;
 * - `#rapport`: het volledige rapport, bovenaan;
 * - `#rapport-6-2`: het volledige rapport, bij hoofdstuk of kop `6-2` (de `key`);
 * - `#nhg`: de NHG-bijlage. `#rapport-nhg`, waar de bijlage vroeger stond, komt ook daar uit.
 *
 * Of een tabblad er is (Kosten en NHG niet als de kosten verborgen zijn), beslist
 * de pagina; een link naar een tabblad dat er niet is, opent het overzicht.
 */
export const REPORT_TABS = ['overzicht', 'kosten', 'rapport', 'nhg'] as const;

export type ReportTab = (typeof REPORT_TABS)[number];

export const REPORT_HASH = 'rapport';

/** Het `id` van een hoofdstuk of kop in het rapport, en dus het anker van een diepe link. */
export function chapterAnchor(key: string): string {
    return `${REPORT_HASH}-${key}`;
}

/** Het `id` van een regel van het overzicht: waar Terug naar overzicht weer uitkomt. */
export function outlineRowId(key: string): string {
    return `overzicht-${key}`;
}

/** Het `id` van een regel van de kostentabel. */
export function costRowId(key: string): string {
    return `kosten-${key}`;
}

/** Het `id` van het paneel onder de tabs. */
export function reportPanelId(idPrefix: string): string {
    return `${idPrefix}-paneel`;
}

export function reportTabId(idPrefix: string, tab: ReportTab): string {
    return `${idPrefix}-tab-${tab}`;
}

export interface ReportView {
    tab: ReportTab;
    /** De plek in het rapport uit de link (`6-2`); `null` zonder. */
    target: string | null;
    /** Een ander tabblad is een nieuwe stap: Terug brengt de koper weer terug. */
    selectTab: (tab: ReportTab) => void;
    /**
     * Zet een plek in het rapport in de adresbalk; `null` is bovenaan. Zonder
     * `push` vervangt hij de huidige stap, met `push` komt er een bij - vanuit het
     * overzicht naar een onderdeel, zodat Terug weer bij het overzicht uitkomt.
     */
    showTarget: (key: string | null, options?: { push?: boolean }) => void;
}

/** Leest de adresbalk: welk tabblad, en welke plek in het rapport. */
export function parseReportHash(hash: string): { tab: ReportTab; target: string | null } {
    const value = decodeHash(hash);

    if (value === 'kosten') {
        return { tab: 'kosten', target: null };
    }

    if (value === 'nhg' || value === chapterAnchor('nhg')) {
        return { tab: 'nhg', target: null };
    }

    if (value === REPORT_HASH) {
        return { tab: 'rapport', target: null };
    }

    if (value.startsWith(`${REPORT_HASH}-`) && value.length > REPORT_HASH.length + 1) {
        return { tab: 'rapport', target: value.slice(REPORT_HASH.length + 1) };
    }

    return { tab: 'overzicht', target: null };
}

export function useReportView(): ReportView {
    const { pathname, search, hash } = useLocation();
    const navigate = useNavigate();
    const { tab, target } = parseReportHash(hash);

    const selectTab = useCallback(
        (next: ReportTab) => {
            navigate({ pathname, search, hash: next });
        },
        [navigate, pathname, search],
    );

    const showTarget = useCallback(
        (key: string | null, options: { push?: boolean } = {}) => {
            navigate(
                { pathname, search, hash: key === null ? REPORT_HASH : chapterAnchor(key) },
                { replace: options.push !== true },
            );
        },
        [navigate, pathname, search],
    );

    return { tab, target, selectTab, showTarget };
}

function decodeHash(hash: string): string {
    const raw = hash.replace(/^#/, '');

    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
}
